import {
  type ExamDefinitionDto,
  type FindingDefinitionDto,
  useExamDefinitions,
  useFindingDefinitions,
} from "@repo/data-store";
import { MarkdownContent } from "@repo/ui/components/markdown-content";
import { createFileRoute, Link } from "@tanstack/react-router";
import { Plus } from "lucide-react";
import { CATALOG_STATUS_OPTIONS } from "@/features/catalog/lib/catalog-status-options";
import { examDefinitionRouteId } from "@/features/catalog/lib/exam-definition-route";
import { canWriteExamDefinitions } from "@/features/catalog/lib/exam-permissions";
import { findingDefinitionRouteId } from "@/features/catalog/lib/finding-definition-route";
import { findingAnswerCountLabel } from "@/features/catalog/lib/finding-shape";
import { ActionButton } from "@/shared/common/action-button";
import { GroupedList, type GroupedListGroup } from "@/shared/common/grouped-list";
import { SearchFilterBar } from "@/shared/common/search-filter-bar";
import { formatDate } from "@/shared/lib/date-formats";
import { StatusPickerBadge } from "@/shared/status/status-picker-badge";

// ============================================================
// Exams and findings share one page
//
// A finding only means anything as something an exam places, and the two are
// authored in one sitting, so they are two groups here rather than two tabs.
// The search box and the status filter drive both queries — a filter that
// narrowed one group and not the other would silently misreport the other.
// ============================================================

export const Route = createFileRoute("/catalog/_layout/exams")({
  component: ExamsComponent,
  validateSearch: (search: Record<string, unknown>): { q?: string; status?: string; group?: string } => ({
    q: (search.q as string) || undefined,
    status: (search.status as string) || undefined,
    group: (search.group as string) || undefined,
  }),
});

type CatalogRow = ExamDefinitionDto | FindingDefinitionDto;

function isFinding(row: CatalogRow): row is FindingDefinitionDto {
  return "kind" in row;
}

function statusOf(row: CatalogRow): "active" | "retired" {
  return row.availability === "Active" ? "active" : "retired";
}

function titleOf(row: CatalogRow): string {
  return row.name;
}

function rowCells(row: CatalogRow) {
  return [
    <div key="title" className="flex min-w-0 flex-col gap-0.5">
      <span className="text-sm font-medium">{titleOf(row)}</span>
      {!isFinding(row) && row.description && (
        <MarkdownContent className="line-clamp-1 text-xs text-muted-foreground">{row.description}</MarkdownContent>
      )}
    </div>,
    <span key="meta" className="text-right text-xs text-muted-foreground">
      {isFinding(row) ? findingAnswerCountLabel(row) : row.authoredAt ? formatDate(row.authoredAt) : null}
    </span>,
    <div key="status" className="flex justify-end">
      <StatusPickerBadge value={statusOf(row)} options={CATALOG_STATUS_OPTIONS} onChange={() => {}} disabled />
    </div>,
  ];
}

function ExamsComponent() {
  const { q: search = "", status: statusFilter, group: focusGroup } = Route.useSearch();
  const navigate = Route.useNavigate();
  const { permissions } = Route.useRouteContext();
  const setSearch = (value: string) =>
    navigate({ search: (prev) => ({ ...prev, q: value || undefined }), replace: true });
  const setStatusFilter = (value: string | null) =>
    navigate({ search: (prev) => ({ ...prev, status: value || undefined }), replace: true });

  const availability = statusFilter
    ? { availability: statusFilter === "active" ? ("Active" as const) : ("Inactive" as const) }
    : {};
  const examDefinitions = useExamDefinitions({ search, ...availability });
  const findingDefinitions = useFindingDefinitions({ search, ...availability });

  const canWriteExams = canWriteExamDefinitions(permissions);
  const canWriteFindings = permissions.canWrite("ObservationDefinition");
  const addAction = (to: "/catalog/exams/new" | "/catalog/findings/new") => () => (
    <ActionButton state={{ allowed: true }} variant="ghost" size="icon-xs" to={to}>
      <Plus className="size-4" />
    </ActionButton>
  );

  const groups: GroupedListGroup<CatalogRow>[] = [
    {
      id: "exams",
      name: "Exams",
      items: examDefinitions,
      ...(canWriteExams ? { headerAction: addAction("/catalog/exams/new") } : {}),
    },
    {
      id: "findings",
      name: "Findings",
      items: findingDefinitions,
      ...(canWriteFindings ? { headerAction: addAction("/catalog/findings/new") } : {}),
    },
  ];

  return (
    <GroupedList
      focusGroup={focusGroup}
      groups={groups}
      isLoading={false}
      header={
        <SearchFilterBar
          search={search}
          onSearchChange={setSearch}
          placeholder="Search exams and findings..."
          statusOptions={[
            { value: "active", label: "Active" },
            { value: "retired", label: "Retired" },
          ]}
          statusValue={statusFilter ?? null}
          onStatusChange={setStatusFilter}
        />
      }
      columns={[
        { id: "title", width: "1fr" },
        { id: "meta", width: "auto" },
        { id: "status", width: "100px" },
      ]}
      columnGap="4px"
      renderCells={rowCells}
      wrapItem={(row, children) =>
        isFinding(row) ? (
          <Link
            to="/catalog/findings/$findingId"
            params={{ findingId: findingDefinitionRouteId(row.definitionUrl) }}
            className="cursor-pointer"
          >
            {children}
          </Link>
        ) : (
          <Link
            to="/catalog/exams/$examId"
            params={{ examId: examDefinitionRouteId(row.definitionUrl) }}
            className="cursor-pointer"
          >
            {children}
          </Link>
        )
      }
      renderItem={(row) => <CatalogRowFallback row={row} />}
      getItemKey={(row) => row.definitionUrl}
      emptyMessage="No exams or findings found"
      isFiltered={!!search || !!statusFilter}
    />
  );
}

/** The non-columnar fallback rendering, for narrow layouts. */
function CatalogRowFallback({ row }: { row: CatalogRow }) {
  return (
    <div className="flex cursor-pointer items-center justify-between px-4 py-3 transition-colors hover:bg-muted/30">
      <div className="flex min-w-0 flex-col gap-0.5">
        <span className="text-sm font-medium">{titleOf(row)}</span>
        {row.description && (
          <MarkdownContent className="line-clamp-1 text-xs text-muted-foreground">{row.description}</MarkdownContent>
        )}
      </div>
      <div className="flex shrink-0 items-center gap-4">
        <span className="text-xs text-muted-foreground">{formatDate(row.authoredAt)}</span>
        <StatusPickerBadge value={statusOf(row)} options={CATALOG_STATUS_OPTIONS} onChange={() => {}} disabled />
      </div>
    </div>
  );
}
